// components/documents/FilePreview.js
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, CircularProgress, Button, Tabs, Tab } from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    FilePresent as FileIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

export const FilePreview = ({ open, onClose, fileUrl, fileName, theme }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('preview');
    const [wordViewerAttempted, setWordViewerAttempted] = useState(false);

    useEffect(() => {
        if (open) {
            setIsLoading(true);
            setError(null);
            setWordViewerAttempted(false);
            // Reset tab to preview when opening new document
            setActiveTab('preview');
        }
    }, [open, fileUrl]);

    const getFileType = () => {
        if (!fileName) return 'unknown';
        const extension = fileName?.toLowerCase().split('.').pop();
        if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image';
        if (extension === 'pdf') return 'pdf';
        if (['doc', 'docx'].includes(extension)) return 'word';
        return 'other';
    };

    const fileType = getFileType();

    const handleLoadComplete = () => {
        setIsLoading(false);
        if (fileType === 'word') {
            setWordViewerAttempted(true);
        }
    };

    const handleLoadError = () => {
        setIsLoading(false);
        if (fileType === 'word') {
            setWordViewerAttempted(true);
            setError(
                'Microsoft Office Online Viewer could not open this document. This could be due to file accessibility, format, or server restrictions.'
            );
        } else {
            setError('Failed to load document preview');
        }
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Using Google Docs as alternative viewer for Word documents
    const getGoogleDocsViewerUrl = () => {
        if (!fileUrl) return null;
        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${window.location.origin}${fileUrl}`;
        return `https://docs.google.com/viewer?url=${encodeURIComponent(fullUrl)}&embedded=true`;
    };

    // Get public URL for Office Online Viewer
    const getOfficeOnlineViewerUrl = () => {
        if (!fileUrl) return null;
        const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${window.location.origin}${fileUrl}`;
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
    };

    // Direct conversion using mammoth.js could be implemented here
    const showAlternativeWordViewer = fileType === 'word' && wordViewerAttempted && error;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ '& .MuiDialog-paper': { height: '90vh' } }}>
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: theme.primary.main,
                    color: 'white',
                    p: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VisibilityIcon />
                    <Typography variant="h6">{fileName || 'File Preview'}</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {showAlternativeWordViewer && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="preview options">
                        <Tab label="Microsoft Viewer" value="preview" />
                        <Tab label="Google Docs Viewer" value="google" />
                        <Tab label="Download" value="download" />
                    </Tabs>
                </Box>
            )}

            <DialogContent sx={{ p: 0, height: '100%', position: 'relative' }}>
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.paper',
                            zIndex: 1
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {/* Standard preview options */}
                {!showAlternativeWordViewer && (
                    <>
                        {error && (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    p: 3
                                }}
                            >
                                <WarningIcon color="warning" sx={{ fontSize: 48 }} />
                                <Typography variant="h6" color="error" align="center">
                                    Preview Error
                                </Typography>
                                <Typography variant="body1" color="text.secondary" align="center">
                                    {error}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        href={fileUrl}
                                        download={fileName}
                                        sx={{
                                            bgcolor: theme.primary.main,
                                            '&:hover': {
                                                bgcolor: theme.primary.dark
                                            }
                                        }}
                                    >
                                        Download File Instead
                                    </Button>
                                </Box>
                                {fileType === 'word' && (
                                    <Box sx={{ mt: 2, width: '100%', maxWidth: 600 }}>
                                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                                            Common issues with Word document previews:
                                        </Typography>
                                        <ul>
                                            <li>The document is not publicly accessible</li>
                                            <li>The document may be password-protected</li>
                                            <li>The document format may not be fully supported</li>
                                            <li>Your server may be blocking access to the document</li>
                                        </ul>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {!error && fileType === 'image' && (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    bgcolor: 'black'
                                }}
                            >
                                <img
                                    src={fileUrl}
                                    alt={fileName}
                                    onLoad={handleLoadComplete}
                                    onError={handleLoadError}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        )}

                        {!error && fileType === 'pdf' && (
                            <iframe
                                src={fileUrl}
                                title={fileName}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                onLoad={handleLoadComplete}
                                onError={handleLoadError}
                            />
                        )}

                        {!error && fileType === 'word' && (
                            <iframe
                                src={getOfficeOnlineViewerUrl()}
                                title={fileName}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                onLoad={handleLoadComplete}
                                onError={handleLoadError}
                            />
                        )}

                        {!error && (fileType === 'other' || fileType === 'unknown') && (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2
                                }}
                            >
                                <FileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                                <Typography variant="body1" color="text.secondary">
                                    This file type cannot be previewed
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    href={fileUrl}
                                    download={fileName}
                                    sx={{
                                        bgcolor: theme.primary.main,
                                        '&:hover': {
                                            bgcolor: theme.primary.dark
                                        }
                                    }}
                                >
                                    Download File
                                </Button>
                            </Box>
                        )}
                    </>
                )}

                {/* Alternative viewers for Word documents */}
                {showAlternativeWordViewer && (
                    <>
                        {activeTab === 'preview' && (
                            <iframe
                                src={getOfficeOnlineViewerUrl()}
                                title={fileName}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        )}

                        {activeTab === 'google' && (
                            <iframe src={getGoogleDocsViewerUrl()} title={fileName} width="100%" height="100%" style={{ border: 'none' }} />
                        )}

                        {activeTab === 'download' && (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 4,
                                    p: 3
                                }}
                            >
                                <FileIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                                <Typography variant="h6">{fileName}</Typography>
                                <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 600 }}>
                                    This document cannot be previewed in the browser. Download it to open in Microsoft Word or another
                                    compatible application.
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<DownloadIcon />}
                                    href={fileUrl}
                                    download={fileName}
                                    sx={{
                                        mt: 2,
                                        bgcolor: theme.primary.main,
                                        '&:hover': {
                                            bgcolor: theme.primary.dark
                                        }
                                    }}
                                >
                                    Download Document
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

FilePreview.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    fileUrl: PropTypes.string,
    fileName: PropTypes.string,
    theme: PropTypes.object.isRequired
};
